import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    IChecklist2ReportDateRange,
    IChecklist2ReportHistoryUnits,
    IChecklistScheduleForReport,
    IChecklistV2ReportParameter,
    IChecklistV2ReportParameterFilter,
} from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { FiChecklist2ReportData } from '@fi-shared/checklist/fi-checklist2-report/shared/fi-checklist2-report-data';
import { takeUntil } from 'rxjs/operators';
import {
    ISelectedFilterItem,
    ISharingMenuData,
} from '@fi-shared/fi-items-list/fi-items-list-filter/fi-items-list-filter/fi-items-list-filter';
import { FiFilterType } from '@fi-shared/fi-items-list/fi-items-list-filter/fi-filter-type-enum';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { cloneDeep, isDefined } from '@fi-shared/utils';
import {
    FiChecklist2ReportType,
    WidgetTableFilterType,
} from '@fi-shared/checklist/fi-checklist2-report/enums/fi-checklist2-report-type';
import { FiChecklist2ReportDataService } from '@fi-shared/checklist/fi-checklist2-report/services/fi-checklist2-report-data.service';
import { FiChecklistReportAvailableBusinessUnitsChecker } from '@fi-shared/checklist/fi-checklist2-report/components/shared/available-business-uints-checker';
import { ChecklistWidgetTableFilterHelperService } from '../../shared/widget-table-filter-helper.service';
import { FiChecklistReportTableFilterComponent } from '../../shared/widget-table-filter/widget-table-filter.component';

export enum UnitPerformanceBenchmarkTab {
    Checklist,
    CheckpointGroup,
    Checkpoint,
}

@Component({
    selector: 'fi-history-checklist-details-unit-performance-benchmark',
    templateUrl: './history-checklist-details-unit-performance-benchmark.component.html',
    styleUrls: ['./history-checklist-details-unit-performance-benchmark.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-history-checklist-details-unit-performance-benchmark' },
    standalone: false,
})
export class FiHistoryChecklistDetailsUnitPerformanceBenchmarkComponent implements OnInit, OnDestroy {
    @Input({ required: true }) mainFilter: IChecklistV2ReportParameterFilter;
    @Input({ required: true }) auditMode: boolean;
    @Input({ required: true }) reportData: FiChecklist2ReportData;
    @Input({ required: true }) reportParameter: IChecklistV2ReportParameter;
    @Input({ required: true }) filterChanged$: Observable<void>;
    @Input('checklistUnits') checklistUnits: IChecklist2ReportHistoryUnits[] = [];
    @Input() availableChecklistScheduleIds$: Observable<number[]>;
    @Output() onBenchmarkFilterChange = new EventEmitter<void>();
    @Output() onShowDetailedAnswersReport = new EventEmitter<IChecklistV2ReportParameterFilter>();

    @ViewChild('checklistReportTableFilter', { static: true })
    checklistReportTableFilter: FiChecklistReportTableFilterComponent;

    benchmarkFilter$ = new BehaviorSubject<IChecklistV2ReportParameterFilter>({} as IChecklistV2ReportParameterFilter);
    filterItemsToRemove$ = new BehaviorSubject<ISelectedFilterItem[]>([]);
    selectedUnitPerformanceBenchmarkTab: UnitPerformanceBenchmarkTab = UnitPerformanceBenchmarkTab.Checklist;
    reportDateRange$ = new BehaviorSubject<IChecklist2ReportDateRange>(undefined);

    protected availableBusinessUnitsChecker: FiChecklistReportAvailableBusinessUnitsChecker;

    isMenuHighlightedFn = (menuItem: ISharingMenuData<INameIdObj>) => {
        return (
            !menuItem.isDisabled &&
            (this.isCheckpointMenuHighlighted(menuItem) ||
                this.isCheckpointGroupMenuHighlighted(menuItem) ||
                this.isUnitMenuHighlighted(menuItem))
        );
    };

    private isCheckpointMenuHighlighted(menuItem: ISharingMenuData<INameIdObj>): boolean {
        return menuItem.filterType === FiFilterType.Checkpoint && !this.benchmarkFilter$.value.CheckpointIds?.length;
    }

    private isCheckpointGroupMenuHighlighted(menuItem: ISharingMenuData<INameIdObj>): boolean {
        return (
            menuItem.filterType === FiFilterType.CheckpointGroup &&
            !this.benchmarkFilter$.value.CheckpointGroupIds?.length
        );
    }

    private isUnitMenuHighlighted(menuItem: ISharingMenuData<INameIdObj>): boolean {
        return (
            menuItem.filterType === FiFilterType.BusinessUnit &&
            (this.selectedUnitPerformanceBenchmarkTab === UnitPerformanceBenchmarkTab.Checkpoint
                ? this.benchmarkFilter$.value.CheckpointIds?.length
                : true) &&
            this.benchmarkFilter$.value.ChecklistIds?.length &&
            !this.benchmarkFilter$.value.BusinessUnitIds?.length
        );
    }

    constructor(
        private injector: Injector,
        private fiChecklist2ReportDataService: FiChecklist2ReportDataService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.reportDateRange$.next(this.fiChecklist2ReportDataService.getDateRange(this.reportParameter.Id));
        this.subscribeOnMainFilterChange();
        this.subscribeOnDateRangeChange();

        // for smart updating available business units on filter change
        this.availableBusinessUnitsChecker = new FiChecklistReportAvailableBusinessUnitsChecker(
            this.injector,
            FiChecklist2ReportType.PastChecklists,
            this.reportData.auditMode,
        );
        this.updateSelectedBusinessUnitsOnAavilableBusinessUnitsChange();
    }

    private updateSelectedBusinessUnitsOnAavilableBusinessUnitsChange(): void {
        this.availableBusinessUnitsChecker.availableBusinessUnitIds$
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((availableBusinessUnitIds: number[]) => {
                if (this.checklistReportTableFilter.isFilterHiddenByType(FiFilterType.BusinessUnit)) {
                    const benchmarkFilter = this.benchmarkFilter$.value;
                    benchmarkFilter.BusinessUnitIds =
                        benchmarkFilter.ChecklistIds?.length && benchmarkFilter.ScheduleIds?.length
                            ? availableBusinessUnitIds
                            : [];

                    this.benchmarkFilter$.next(benchmarkFilter);
                }
            });
    }

    private updateReportDateRangeFn = () =>
        this.reportDateRange$.next(cloneDeep(this.fiChecklist2ReportDataService.getDateRange(this.reportParameter.Id)));

    private subscribeOnDateRangeChange() {
        this.fiChecklist2ReportDataService.subscribeOnFilterOrDateRangeChanged(
            this.reportParameter.Id,
            this.ngOnDestroy$,
            this.updateReportDateRangeFn,
        );
    }

    private subscribeOnMainFilterChange() {
        this.filterChanged$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            const selectedMainChecklistIds: number[] = this.mainFilter.ChecklistIds;
            const benchmarkFilter: IChecklistV2ReportParameterFilter = this.benchmarkFilter$.value;
            if (benchmarkFilter.ChecklistIds?.length) {
                const checklistIdsToRemove: number[] = benchmarkFilter.ChecklistIds.filter((checklistId: number) => {
                    return !selectedMainChecklistIds.includes(checklistId);
                });
                if (checklistIdsToRemove?.length) {
                    this.filterItemsToRemove$.next(
                        checklistIdsToRemove.map(
                            (checklistId: number): ISelectedFilterItem => ({
                                value: checklistId,
                                filterType: FiFilterType.Checklist,
                            }),
                        ),
                    );
                }
            }
            this.cdr.detectChanges();
        });
    }

    getWidgetTableFilterType() {
        if (this.selectedUnitPerformanceBenchmarkTab === UnitPerformanceBenchmarkTab.Checklist)
            return WidgetTableFilterType.UnitBenchmarkByChecklists;
        if (this.selectedUnitPerformanceBenchmarkTab === UnitPerformanceBenchmarkTab.CheckpointGroup)
            return WidgetTableFilterType.UnitBenchmarkByCheckpointGroups;
        if (this.selectedUnitPerformanceBenchmarkTab === UnitPerformanceBenchmarkTab.Checkpoint)
            return WidgetTableFilterType.UnitBenchmarkByCheckpoints;
        throw new Error(
            `getWidgetTableFilterType: unknown UnitPerformanceBenchmarkTab ${this.selectedUnitPerformanceBenchmarkTab}`,
        );
    }

    isUnitPerformanceBenchmarkTabSelected(value: UnitPerformanceBenchmarkTab): boolean {
        return this.selectedUnitPerformanceBenchmarkTab === value;
    }

    filterReportData(filter: IChecklistV2ReportParameterFilter) {
        this.benchmarkFilter$.next(Object.assign({}, filter));
        this.onBenchmarkFilterChange.emit();

        this.availableBusinessUnitsChecker.setAvailableBusinessUnitIds(
            undefined,
            filter,
            this.fiChecklist2ReportDataService.getDateRange(this.reportParameter.Id),
            true,
        );
    }

    isFilterEmpty(): boolean {
        const benchmarkFilterData: IChecklistV2ReportParameterFilter = this.benchmarkFilter$.value;
        return (
            !benchmarkFilterData.BusinessUnitIds?.length ||
            (this.isUnitPerformanceBenchmarkTabSelected(UnitPerformanceBenchmarkTab.Checkpoint) &&
                !benchmarkFilterData.CheckpointIds?.length) ||
            (this.isUnitPerformanceBenchmarkTabSelected(UnitPerformanceBenchmarkTab.CheckpointGroup) &&
                !benchmarkFilterData.CheckpointGroupIds?.length)
        );
    }

    private prevFilterCheckpointIds: number[];
    private prevFilterCheckpointGroupIds: number[];
    onSelectedUnitPerformanceBenchmarkTabChanged(benchmarkTab: UnitPerformanceBenchmarkTab) {
        const benchmarkFilter: IChecklistV2ReportParameterFilter = cloneDeep(this.benchmarkFilter$.value);
        if (isDefined(benchmarkFilter.CheckpointIds)) {
            this.prevFilterCheckpointIds = benchmarkFilter.CheckpointIds;
            delete benchmarkFilter.CheckpointIds;
        }
        if (isDefined(benchmarkFilter.CheckpointGroupIds)) {
            this.prevFilterCheckpointGroupIds = benchmarkFilter.CheckpointGroupIds;
            delete benchmarkFilter.CheckpointGroupIds;
        }
        if (benchmarkTab === UnitPerformanceBenchmarkTab.Checkpoint) {
            benchmarkFilter.CheckpointIds = this.prevFilterCheckpointIds;
        } else if (benchmarkTab === UnitPerformanceBenchmarkTab.CheckpointGroup) {
            benchmarkFilter.CheckpointGroupIds = this.prevFilterCheckpointGroupIds;
        }
        this.benchmarkFilter$.next(benchmarkFilter);
    }

    showDetailedAnswersReport(detailedReportFilter: IChecklistV2ReportParameterFilter) {
        this.onShowDetailedAnswersReport.emit(detailedReportFilter);
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    protected readonly UnitPerformanceBenchmarkTab = UnitPerformanceBenchmarkTab;
    protected readonly WidgetTableFilterType = WidgetTableFilterType;
    protected readonly FiFilterType = FiFilterType;
}
